import { API_BASE_URL } from '../../constants/appConstants';
import { authenticatedRequest } from '../authService';

const SUPERADMIN_BASE_URL = `${API_BASE_URL}/superadmin`;

/**
 * Fetch paginated list of users with search and filtering
 * @param {Object} params - Query parameters
 * @param {string} params.search - Search term for username, email, or name
 * @param {string} params.status - Status filter (all, active, inactive, pending)
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.page_size - Items per page (default: 10)
 * @returns {Promise<Object>} Response with users list and pagination info
 */
export const fetchUsers = async (params = {}) => {
  const {
    search = '',
    status = 'all',
    page = 1,
    page_size = 10
  } = params;

  // Build query string
  const queryParams = new URLSearchParams();
  if (search.trim()) queryParams.append('search', search.trim());
  if (status !== 'all') queryParams.append('status', status);
  queryParams.append('page', page.toString());
  queryParams.append('page_size', page_size.toString());

  const url = `${SUPERADMIN_BASE_URL}/users/?${queryParams.toString()}`;

  try {
    const response = await authenticatedRequest(url, {
      method: 'GET',
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch users');
    }

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching users:', error);
    throw new Error(error.message || 'Failed to fetch users');
  }
};

/**
 * Fetch detailed information for a specific user
 * @param {number} userId - User ID
 * @returns {Promise<Object>} Response with user details
 */
export const fetchUserDetails = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  const url = `${SUPERADMIN_BASE_URL}/users/${userId}/`;

  try {
    const response = await authenticatedRequest(url, {
      method: 'GET',
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch user details');
    }

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching user details:', error);
    throw new Error(error.message || 'Failed to fetch user details');
  }
};

/**
 * Toggle user active status (activate/deactivate)
 * @param {number} userId - User ID
 * @returns {Promise<Object>} Response with updated user data
 */
export const activateDeactivateUser = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  const url = `${SUPERADMIN_BASE_URL}/users/${userId}/activate-deactivate-status/`;

  try {
    const response = await authenticatedRequest(url, {
      method: 'POST',
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to toggle user status');
    }

    return {
      success: true,
      data: response.data,
      message: response.data.message
    };
  } catch (error) {
    console.error('Error toggling user status:', error);
    throw new Error(error.message || 'Failed to toggle user status');
  }
};



/**
 * Fetch user financial summary (total counts and amounts)
 * @param {number} userId - User ID
 * @returns {Promise<Object>} Response with financial summary
 */
export const fetchUserFinancialSummary = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  const url = `${SUPERADMIN_BASE_URL}/users/${userId}/financial-summary/`;

  try {
    const response = await authenticatedRequest(url, {
      method: 'GET',
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch user financial summary');
    }

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching user financial summary:', error);
    throw new Error(error.message || 'Failed to fetch user financial summary');
  }
};

/**
 * Fetch user monthly comparison data
 * @param {number} userId - User ID
 * @returns {Promise<Object>} Response with monthly comparison data
 */
export const fetchUserMonthlyComparison = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  const url = `${SUPERADMIN_BASE_URL}/users/${userId}/monthly-comparison/`;

  try {
    const response = await authenticatedRequest(url, {
      method: 'GET',
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch user monthly comparison');
    }

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching user monthly comparison:', error);
    throw new Error(error.message || 'Failed to fetch user monthly comparison');
  }
};

/**
 * Fetch user recent activities
 * @param {number} userId - User ID
 * @returns {Promise<Object>} Response with recent activities
 */
export const fetchUserRecentActivities = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  const url = `${SUPERADMIN_BASE_URL}/users/${userId}/recent-activities/`;

  try {
    const response = await authenticatedRequest(url, {
      method: 'GET',
    });

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch user recent activities');
    }

    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching user recent activities:', error);
    throw new Error(error.message || 'Failed to fetch user recent activities');
  }
};